<template>
  <div
    ref="mainMenu"
    class="c-main-menu leading-none z-20"
    :class="{
      'is-opened-overlay': isOverlayVisible,
      'has-no-animation': isAnimated,
      'is-opened': isMobileOpened,
      'is-closing': isClosing,
      'is-submenu-opened': isSubmenuOpened
    }"
  >
    <div
      class="c-main-menu__overlay"
      @click="hideOpenedSubmenu()"
    />
    <button
      v-if="!isSubmenuOpened"
      type="button"
      aria-label="Menu toggle"
      aria-haspopup="true"
      class="c-main-menu__button flex items-center xl:hidden"
      @click="toggleButton"
    >
      <span
        v-if="!isMobileOpened"
        class="ltr:mr-2 rtl:ml-2 text-blue-dark"
      >
        {{ model.menuTitle }}
      </span>
      <span
        v-else
        :class="model.menuClose ? 'ltr:mr-2 rtl:ml-2 text-blue-dark' : ''"
      >
        {{ model.menuClose }}
      </span>

      <span
        class="c-main-menu__button-icon"
        :title="model.menuTitle"
        :class="{ 'is-menu-opened': isMobileOpened, 'flip-x': direction === 'rtl' }"
      >
        <span class="c-main-menu__button-line-1" />
        <span class="c-main-menu__button-line-2" />
        <span class="c-main-menu__button-line-3" />
      </span>
    </button>

    <button
      v-if="isSubmenuOpened"
      class="c-main-menu__close-button flex items-center pt-2 sm:pt-0"
      @click="backToMainMenu"
    >
      <span
        class="xl:hidden text-sm text-white block transform rotate-180"
        :class="{
          'icon-arrow-left': direction === 'rtl',
          'icon-arrow-right-new': direction === 'ltr'
        }"
      />
    </button>
    <span
      v-if="isNotDesktop"
      class="c-main-menu__submenu-title pt-3 sm:pt-0"
      v-text="activeSectionTitle"
    />
    <div
      ref="menuWrap"
      class="c-main-menu-wrap"
    >
      <div class="c-main-menu-inner">
        <ul
          ref="nav"
          class="c-main-menu__list"
        >
          <li
            v-for="(section, index) in navigation"
            :key="section.name"
            class="c-main-menu__item"
            :class="{
              'is-active-item': index === openIndex || section.isSubmenuOpened
            }"
            @mouseenter="onMenuItemHover(section, index)"
            @mouseleave="onMenuItemLeave(section, index)"
          >
            <MenuItem
              class="c-main-menu__item-link group font-normal ltr:pr-8 ltr:pl-4 rtl:pr-2 rtl:pl-4"
              :item="section"
              :new-label="newLabel"
              @click="onMenuItemClick($event, section, section.name)"
            >
              <template #prefix>
                <span
                  v-if="direction === 'rtl'"
                  class="xl:hidden absolute top-0 right-0 h-full bg-blue-dark w-1 transform duration-200 translate-x-2 group-hover:translate-x-0"
                />
                <span
                  v-else
                  class="xl:hidden absolute top-0 left-0 h-full bg-blue-dark w-1 transform duration-200 -translate-x-2 group-hover:translate-x-0"
                />
              </template>
              <span
                v-if="section.children && section.children.length"
                class="xl:hidden transform duration-200 group-hover:-translate-x-2 text-3xs"
                :class="{
                  'icon-arrow-left': direction === 'rtl',
                  'icon-arrow-right-new': direction === 'ltr'
                }"
              />
            </MenuItem>
            <SubMenu
              :section="section"
              :new-label="newLabel"
              :is-active="isSubmenuOpened && section.isSubmenuOpened"
            />
          </li>
          <li
            v-if="model.adimoButton"
            class="c-main-menu__item--adimo-buy-button"
          >
            <AdimoButton
              :model="model.adimoButton"
              button-css-class="btn"
            />
          </li>
          <li class="xl:hidden h-20 bg-white w-full curve-bottom--sub-element" />
        </ul>

        <MenuFeatured
          v-if="featuredNavigation && isNotDesktop"
          :model="featuredNavigation"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { disablePageScroll, enablePageScroll } from 'scroll-lock'
import debugMenuItem from '@/utils/useDebug'
import tracking from '@/helpers/tracking'
import { isNotDesktop } from '@/lib/is-mobile'
import MenuItem from '@/components/MenuItem.vue'
import MenuFeatured from '@/components/MenuFeatured.vue'
import SubMenu from '@/components/SubMenu.vue'
import AdimoButton from '@/components/AdimoButton.vue'

const debuggingItem = debugMenuItem()
const debugging = debuggingItem >= 0


export default {
  components: {
    AdimoButton,
    MenuItem,
    MenuFeatured,
    SubMenu
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    direction: {
      type: String,
      default: 'ltr'
    },
    newLabel: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      navigation: this.model.menuItems,
      body: document.body,
      openIndex: debuggingItem || -1,
      timer: null,
      isAnimated: false,
      animatedTimer: null,
      isOverlayVisible: debuggingItem,
      isMobileOpened: debugging,
      isClosing: false,
      isSubmenuOpened: debugging,
      activeSectionTitle: '',
      isNotDesktop: isNotDesktop()
    }
  },
  computed: {
    featuredNavigation() {
      if (!this.navigation.length) {
        return null
      }
      const { header, items } = this.navigation[0].highlightedItems
      return items.length ? {
        header,
        items: items.map((item) => ({
          ...item,
          cardType: 'featuredMenuItem'
        }))
      } : null
    }
  },
  watch: {
    isMobileOpened(isOpened) {
      if (isOpened) {
        this.body.classList.add('is-navigation-opened')
        if (this.isNotDesktop) {
          disablePageScroll(this.$refs.menuWrap)
        }
      } else {
        this.body.classList.remove('is-navigation-opened')
        if (this.isNotDesktop) {
          enablePageScroll(this.$refs.menuWrap)
        }
      }
    }
  },
  created() {
    document.addEventListener('click', this.documentClick)
  },
  mounted() {
    window.addEventListener('resize', this.checkBreakpoint, false)
    window.addEventListener('orientationchange', this.checkBreakpoint, false)
  },
  destroyed() {
    document.removeEventListener('click', this.documentClick)
    window.removeEventListener('resize', this.checkBreakpoint, false)
    window.removeEventListener('orientationchange', this.checkBreakpoint, false)
  },
  methods: {
    checkBreakpoint() {
      this.isNotDesktop = isNotDesktop()
    },
    onMenuItemHover(section, index) {
      if (this.isNotDesktop || debugging) {
        return
      }
      if (this.timer) {
        clearTimeout(this.timer)
      }
      if (this.delayTimer) {
        clearTimeout(this.delayTimer)
      }

      if (section.children?.length) {
        if (this.openIndex === -1) {
          this.delayTimer = setTimeout(() => {
            this.isOverlayVisible = true
            this.$refs.mainMenu.parentNode.classList.add('menu-opened')
            this.openIndex = index
          }, 200)
        } else {
          this.isOverlayVisible = true
          this.$refs.mainMenu.parentNode.classList.add('menu-opened')
          if (this.openIndex !== -1) {
            this.isAnimated = true
          }
          this.openIndex = index
        }
      } else {
        this.openIndex = -1
        this.isOverlayVisible = false
        setTimeout(() => {
          this.$refs.mainMenu.parentNode.classList.remove('menu-opened')
        }, 400)
      }
    },
    onMenuItemLeave() {
      if (this.isNotDesktop || debugging) {
        return
      }
      if (this.delayTimer) {
        clearTimeout(this.delayTimer)
      }
      this.timer = setTimeout(() => {
        this.isAnimated = false
        this.openIndex = -1
        this.isOverlayVisible = false
        setTimeout(() => {
          this.$refs.mainMenu.parentNode.classList.remove('menu-opened')
        }, 400)
      }, 200)
    },
    toggleButton() {
      if (this.isMobileOpened) {
        this.isClosing = true
        this.isMobileOpened = false
        this.isSubmenuOpened = false

        setTimeout(() => {
          this.isClosing = false
          this.hideOpenedSubmenu()
        }, 300)
        tracking.mainMenuNavigationClose()
      } else {
        this.isMobileOpened = true
        tracking.mainMenuNavigationOpen()
      }
    },
    hideOpenedSubmenu() {
      this.activeSectionTitle = ''
      const openedSubmenu = this.navigation.find((section) => section.isSubmenuOpened)
      if (openedSubmenu) {
        openedSubmenu.isSubmenuOpened = false
      }
    },
    onMenuItemClick(event, section) {
      let trackingEvent = 'topMenuTitleLinkClick'
      if (this.isNotDesktop) {
        if (section.children?.length) {
          event.preventDefault()
          this.hideOpenedSubmenu()
          if (this.$refs.menuWrap) {
            this.$refs.menuWrap.scrollTo(0, 0)
          }
          this.isSubmenuOpened = true
          section.isSubmenuOpened = true
          this.activeSectionTitle = section.name
          trackingEvent = 'topMenuTitleMobileDropdownClick'
        } else {
          trackingEvent = 'topMenuTitleMobileLinkClick'
        }
      }
      tracking.mainMenuItemClick(trackingEvent, section.title)
    },
    backToMainMenu() {
      this.isSubmenuOpened = false
      tracking.mainMenuPreviousClick()
      this.hideOpenedSubmenu()
    },
    documentClick(e) {
      if (this.isSubmenuOpened) {
        const el = this.$refs.nav
        const { target } = e
        if (el !== target && !el.contains(target)) {
          this.isSubmenuOpened = false
          this.hideOpenedSubmenu()
        }
      }
    }
  }
}
</script>
